import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const url = (t) => `${process.env.REACT_APP_BACKEND_URL}/api/public/po-confirm/${t}`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Lines = ({ lines }) => (
  <table className="w-full text-sm" data-testid="pc-lines">
    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left"><th className="py-1">Item</th><th className="text-right">Qty</th><th className="text-right">Unit</th></tr></thead>
    <tbody>{lines.map((l, i) => (
      <tr key={i} className="border-t border-slate-100"><td className="py-1.5">{l.description}{l.vendor_part_number && <div className="text-[11px] text-slate-500 font-mono">Your P/N {l.vendor_part_number}</div>}</td>
        <td className="text-right font-mono">{l.quantity}</td><td className="text-right font-mono">{money(l.unit_cost)}</td></tr>
    ))}</tbody>
  </table>
);

const Done = ({ c, d }) => (
  <div className={`p-4 rounded-sm border ${c.status === 'confirmed' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="pc-done">
    <div className="flex items-center gap-2 font-semibold">{c.status === 'confirmed' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
      {c.status === 'confirmed' ? `Thank you, ship date ${c.ship_date} received.` : `Thank you, we've been notified: ${c.problem_type}.`}</div>
    <p className="text-xs text-slate-600 mt-1">{d.company_name} purchasing has been notified. You can update your response below at any time before the link expires.</p>
  </div>
);

export default function PublicPoConfirmPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('confirm');
  const [f, setF] = useState({ ship_date: '', vendor_reference: '', note: '', problem_type: '', contact_name: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(url(token)).then(r => {
      setD(r.data);
      const c = r.data.confirmation;
      if (c) { setMode(c.status === 'problem' ? 'problem' : 'confirm'); setF({ ship_date: c.ship_date || '', vendor_reference: c.vendor_reference || '', note: c.note || '', problem_type: c.problem_type || '', contact_name: c.contact_name || '' }); }
      else setF(x => ({ ...x, ship_date: r.data.expected_date || '' }));
    }).catch(e => setErr(e.response?.data?.detail || 'This link is not valid'));
  }, [token]);

  const submit = async () => {
    setBusy(true); setMsg('');
    try { const r = await axios.post(url(token), { action: mode, ...f }); setD({ ...d, confirmation: r.data.confirmation }); }
    catch (e) { setMsg(e.response?.data?.detail || 'Could not submit, please try again'); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (err) return <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6"><p className="text-red-600 font-semibold text-center" data-testid="pc-error">{err}</p></div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="public-po-confirm-page">
      <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-sm shadow-sm">
        <div className="flex items-center gap-3 p-5 border-b border-slate-200">
          {d.logo_data && <img src={d.logo_data} alt={d.company_name} className="h-10 max-w-[140px] object-contain" />}
          <div className="flex-1"><div className="font-bold text-lg">{d.company_name}</div><div className="text-xs text-slate-500">Purchase Order confirmation</div></div>
          <div className="text-right"><div className="font-mono font-bold text-lg" data-testid="pc-po-number">{d.po_number}</div><div className="text-xs text-slate-500">Ordered {d.order_date}</div></div>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Vendor</div>{d.vendor_name}</div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Ship to</div><div className="whitespace-pre-line">{d.ship_to}</div></div>
          </div>
          <Lines lines={d.lines} />
          <div className="text-right text-sm">PO total <b className="font-mono">{money(d.total)}</b></div>
          {d.confirmation && <Done c={d.confirmation} d={d} />}
          <div className="flex gap-2">
            <Button variant={mode === 'confirm' ? 'default' : 'outline'} onClick={() => setMode('confirm')} className="rounded-sm flex-1" data-testid="pc-mode-confirm"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm order</Button>
            <Button variant={mode === 'problem' ? 'default' : 'outline'} onClick={() => setMode('problem')} className="rounded-sm flex-1" data-testid="pc-mode-problem"><AlertTriangle className="w-4 h-4 mr-1" /> Report a problem</Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {mode === 'confirm' ? (
              <div><Label>Ship date *</Label><Input type="date" value={f.ship_date} onChange={set('ship_date')} className="rounded-sm" data-testid="pc-ship-date" /></div>
            ) : (
              <div><Label>Problem *</Label>
                <select value={f.problem_type} onChange={set('problem_type')} className="w-full h-9 border border-slate-200 rounded-sm px-2 text-sm bg-white" data-testid="pc-problem-type">
                  <option value="">— select —</option>{d.problem_types.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            )}
            <div><Label>Your order / reference #</Label><Input value={f.vendor_reference} onChange={set('vendor_reference')} maxLength={80} className="rounded-sm" data-testid="pc-vendor-ref" /></div>
            {mode === 'problem' && <div><Label>Earliest possible ship date</Label><Input type="date" value={f.ship_date} onChange={set('ship_date')} className="rounded-sm" data-testid="pc-alt-date" /></div>}
            <div><Label>Your name</Label><Input value={f.contact_name} onChange={set('contact_name')} maxLength={120} className="rounded-sm" data-testid="pc-contact-name" /></div>
            <div className="sm:col-span-2"><Label>Note {mode === 'problem' && f.problem_type === 'Other' ? '*' : ''}</Label><Textarea rows={3} value={f.note} onChange={set('note')} maxLength={2000} placeholder={mode === 'problem' ? 'e.g. new price, revised lead time, substitute part' : 'Optional'} className="rounded-sm" data-testid="pc-note" /></div>
          </div>
          {msg && <p className="text-sm text-red-600" data-testid="pc-msg">{msg}</p>}
          <Button onClick={submit} disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="pc-submit">{busy ? 'Sending…' : d.confirmation ? 'Update response' : mode === 'confirm' ? 'Confirm order' : 'Send problem report'}</Button>
          <p className="text-[11px] text-slate-500 text-center">Questions? Contact {d.contact_email}. Link valid until {d.expires_at?.slice(0, 10)}.</p>
        </div>
      </div>
    </div>
  );
}
